import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from "react-router-dom";
import {
    Button, Menu, MenuItem, Tab, Typography,
    Badge, Box, IconButton, ListItemIcon, ListItemText, Avatar, Paper, Tooltip
} from "@mui/material";
import TabList from "@mui/lab/TabList";
import TabContext from "@mui/lab/TabContext";
import TabPanel from "@mui/lab/TabPanel";
import _ from 'lodash';

// State & Context
import { PAContext } from "../../../Services/PAContext";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";

// Styles & Helpers
import './supplies.css';
import {capitalizeFirstLetter, stringAvatar, stringToColor} from "../../Helpers/TextStyling";

// Dialog Components
import InfoSupplies from "../../Dialogs/InfoSupplies";
import SuppliesAddNewDialog from './SuppliesAddNewDialog';
import SuppliesAddSuggestionsDialog from './SuppliesAddSuggestionsDialog';
import SuppliesAddEssentialsDialog from './SuppliesAddEssentialsDialog';
import {getAllContactsFromDatabase} from "../../Restricted/Contacts/BrowserDatabaseFunctions";
import SuppliesEditItemDialog from "./SuppliesEditItemDialog";
import Divider from "@mui/material/Divider";
import {Info, Plus} from "lucide-react";



function SuppliesList(props) {
    const navigate = useNavigate();
    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();
    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { Account, SampleAttendees, authenticated } = dataArray;


    // --- State: Dialog Management ---
    const [isAddManualOpen, setIsAddManualOpen] = useState(false);
    const [isAddSuggestionsOpen, setIsAddSuggestionsOpen] = useState(false);
    const [isAddEssentialsOpen, setIsAddEssentialsOpen] = useState(false);
    const [isSuppliesInfoDialogOpen, setSuppliesInfoDialogOpen] = useState(false);

    // --- State: UI Management ---
    const [selectedAttendee, setSelectedAttendee] = useState(Account.id || "0");
    const [tabValue, setTabValue] = useState('supplies');
    const [anchorSuppliesMenu, setAnchorSuppliesMenu] = useState(null);
    const [anchorSupplyItemListMenu, setAnchorSupplyItemListMenu] = useState(null);
    const [selectedItemId, setSelectedItemId] = useState(null);

    // --- Logic: Attendee Lists & Sorting ---
    const [dbContacts, setDbContacts] = useState([]);
    useEffect(() => {
        if (!authenticated) {
            setDbContacts([]);
            return;
        }

        let cancelled = false;

        getAllContactsFromDatabase()
            .then(contacts => {
                if (cancelled) return;
                setDbContacts(Array.isArray(contacts) ? contacts : []);
            })
            .catch(err => {
                console.error('Failed to load Contacts from browser DB:', err);
                if (!cancelled) {
                    setDbContacts([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [authenticated]);
    const [ attendeesReferenceArray, setAttendeesReferenceArray ] = useState( authenticated
        ? dbContacts
        : SampleAttendees);
    useEffect(() => {

        console.log("authenticated, dbContacts, SampleAttendees triggered ...");

        setAttendeesReferenceArray(
            authenticated
                ? dbContacts
                : SampleAttendees
        );
    }, [authenticated, dbContacts, SampleAttendees]);

    const sortedAttendees = useMemo(() => {
        const supplies = PA_GlobalObject?.Planning?.supplies || [];
        const list = (PA_GlobalObject?.Planning?.attendees || [])
            .filter(a => a.role !== 'planner')
            .map(attendee => {
                const count = supplies.reduce((c, item) =>
                    c + (item.assigned_to === attendee.id && (!item.private || item.assigned_to === Account.id) ? 1 : 0), 0
                );
                return { ...attendee, supplyCount: count };
            });

        const withSupplies = list.filter(a => a.supplyCount > 0).sort((a, b) => b.supplyCount - a.supplyCount);
        const withoutSupplies = list.filter(a => a.supplyCount === 0).sort((a, b) => (a.nickname || "").localeCompare(b.nickname || ""));
        return [...withSupplies, ...withoutSupplies];
    }, [PA_GlobalObject?.Planning?.supplies, PA_GlobalObject?.Planning?.attendees, Account.id]);

    const unassignedCount = useMemo(() =>
            (PA_GlobalObject?.Planning?.supplies || []).filter(item => item.assigned_to === "0").length
        , [PA_GlobalObject?.Planning?.supplies]);

    // --- Logic: Grouped Items for Tabs ---
    const groupedItems = useMemo(() => {
        const supplies = PA_GlobalObject?.Planning?.supplies || [];

        const filtered = supplies.filter(item => {
            if (selectedAttendee === "0") return item.assigned_to === "0";
            if (item.assigned_to === selectedAttendee) {
                return !(item.private && item.assigned_to !== Account.id);
            }
            return false;
        });

        const alphabeticallySorted = _.sortBy(filtered, [(item) => item.name.toLowerCase()]);

        return _.groupBy(alphabeticallySorted, item => {
            // NORMALIZE the strings to lowercase and trim any accidental spaces
            const cat = item.category?.trim().toLowerCase() || "unassigned";
            const subCat = item.sub_category?.trim().toLowerCase() || "general";

            // Return the unified key
            return `${cat}_${subCat}`;
        });
    }, [PA_GlobalObject?.Planning?.supplies, selectedAttendee, Account.id]);

    const totalSupplies = _.sumBy(Object.keys(groupedItems).filter(k => k.startsWith('supplies')), k => groupedItems[k].length);
    const totalFood = _.sumBy(Object.keys(groupedItems).filter(k => k.startsWith('food')), k => groupedItems[k].length);

    // --- Handlers: Item Actions ---
    const toggleItemStatus = (itemId) => {

        set_PA_GlobalObject(prev => ({
            ...prev,
            Planning: {
                ...prev.Planning,
                supplies: prev.Planning.supplies.map(i => i.id === itemId ? { ...i, status: i.status === 0 ? 1 : 0 } : i)
            }
        }));

        if (PA_GlobalObject.Planning.status !== 10) {
            const item = PA_GlobalObject.Planning.supplies.find(i => i.id === itemId);
            sendSocketMessage({
                action: "E_updateSupplyItemStatus",
                event_id: PA_GlobalObject.Planning.id,
                item_id: itemId,
                private: item.private,
                status: item.status === 0 ? 1 : 0
            });
        }
    };

    // --- Add these to your State section ---
    const [editingItem, setEditingItem] = useState(null); // The full item object being edited
    const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);

    // --- The Master Update Function ---
    const handleUpdateItem = (updatedItem) => {
        set_PA_GlobalObject((prev) => ({
            ...prev,
            Planning: {
                ...prev.Planning,
                supplies: prev.Planning.supplies.map((item) =>
                    item.id === updatedItem.id ? updatedItem : item
                ),
            },
        }));

        if (PA_GlobalObject.Planning.status !== 10) {
            sendSocketMessage({
                action: "E_updateSupplyItem", // Ensure your backend handles the full object update
                event_id: PA_GlobalObject.Planning.id,
                supply_item: updatedItem,
            });
        }
        setIsEditDrawerOpen(false);
    };

    const handleSupplyItemDelete = (itemId) => {

        set_PA_GlobalObject(prev => ({
            ...prev,
            Planning: {
                ...prev.Planning,
                supplies: prev.Planning.supplies.filter(i => i.id !== itemId)
            }
        }));

        if (PA_GlobalObject.Planning.status !== 10) {
            sendSocketMessage({
                action: "E_deleteSupplyItem",
                event_id: PA_GlobalObject.Planning.id,
                item_id: itemId });
        }

        setAnchorSupplyItemListMenu(null);

        setIsEditDrawerOpen(false);        // ✅ THE FIX: This closes your edit Dialog/Drawer
        setEditingItem(null);              // Clear the reference to the deleted item
    };

    const handleClaimItem = (item) => {

        set_PA_GlobalObject(prev => ({
            ...prev,
            Planning: { ...prev.Planning, supplies: prev.Planning.supplies.map(s => s.id === item.id ? { ...s, assigned_to: Account.id } : s) }
        }));

        if (PA_GlobalObject.Planning.status !== 10) {
            sendSocketMessage({
                action: "E_updateSupplyItemAssignedTo",
                event_id: PA_GlobalObject.Planning.id,
                item_id: item.id,
                assigned_to: Account.id
            });
        }
    };

    // --- Render Helper ---
    const renderSupplyItem = (item) => {
        const isOwner = item.assigned_to === Account.id;
        const isUnassigned = item.assigned_to === "0";
        const adder = attendeesReferenceArray.find(a => a.id === item.assigned_by);

        return (
            <Box
                key={item.id}
                sx={{
                    p: '8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    '&:hover': { bgcolor: '#f8fafc' }
                }}
            >

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
                    {!isUnassigned && (
                        <IconButton size="small"
                                    onClick={(e) => {
                                        e.stopPropagation(); // Don't trigger the Edit Dialog
                                        toggleItemStatus(item.id);
                                    }}
                        >
                            <i className={item.status === 0 ? "icon-check-square" : "icon-checkbox-unchecked"}
                               style={{ color: item.status === 0 ? '#bdc3c7' : '#4caf50', fontSize: '18px' }} />
                        </IconButton>
                    )}
                    {isUnassigned && <i className="icon-notification" style={{ color: '#e74c3c' }} />}
                    <Box>
                        <Typography variant="body2" sx={{ textDecoration: item.status === 0 ? 'line-through' : 'none', fontWeight: isUnassigned ? 700 : 400 }}>
                            {capitalizeFirstLetter(item.name)}
                        </Typography>
                        {item.private && <i className="icon-lock" style={{ fontSize: '10px', color: '#f39c12', marginLeft: '5px' }} />}
                        {item.notes && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{item.notes}</Typography>}
                        {item.assigned_by !== item.assigned_to && adder && (
                            <Typography variant="caption" sx={{ fontStyle: 'italic', color: '#95a5a6' }}>Added by {adder.nickname || adder.fname}</Typography>
                        )}
                    </Box>
                </Box>
                <Box>
                    {isUnassigned ? (
                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClaimItem(item);
                            }}
                        >
                            Claim
                        </Button>
                    ) : (
                        isOwner && (
                            <IconButton
                                size="small"
                                sx={{
                                    bgcolor: '#f1f5f9',
                                    '&:hover': { bgcolor: '#e2e8f0', color: '#6366f1' }
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingItem(item); // Load the item into state
                                    setIsEditDrawerOpen(true); // Open the Edit Dialog
                                }}
                            >
                                <i className="icon-pencil" style={{ fontSize: '14px' }} />
                            </IconButton>
                        )
                    )}
                </Box>
            </Box>
        );
    };



    return (
        <div className="supplies-list-container">

            {/* --- SUPPLIES HEADER --- */}
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                    boxShadow: '0 4px 24px -6px rgba(0,0,0,0.06)',
                    borderRadius: '12px 12px 0 0', // Keep the top rounded for the list container
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                    {/* Title Area */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', // Gold/Amber gradient for supplies
                            p: 0.9,
                            borderRadius: '10px',
                            display: 'flex',
                            boxShadow: '0 4px 10px rgba(245, 158, 11, 0.3)'
                        }}>
                            {/* Swap Package with whatever icon library you are using (e.g., Lucide or Font-icon) */}
                            <i className="icon-checklist2" style={{ color: 'white', fontSize: '18px' }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1rem', letterSpacing: -0.4, whiteSpace: 'nowrap' }}>
                                Supplies
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                <i className="icon-check-circle" style={{ color: '#d97706', fontSize: '10px' }} />
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.65rem', letterSpacing: 0.1, whiteSpace: 'nowrap' }}>
                                    Inventory & Logistics
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Controls Area */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                    }}>

                        {/* Info Icon */}
                        <Tooltip title="About Supplies">
                            <IconButton
                                onClick={() => setSuppliesInfoDialogOpen(true)}
                                sx={{ color: '#64748b' }}
                            >
                                <Info size={20} />
                            </IconButton>
                        </Tooltip>

                        {/* Menu Add Button */}
                        <Tooltip title="Add Items">
                            <IconButton
                                onClick={(e) => setAnchorSuppliesMenu(e.currentTarget)}
                                sx={{
                                    ml: 0.5,
                                    bgcolor: '#1e293b',
                                    color: 'white',
                                    borderRadius: '12px',
                                    width: '38px',
                                    height: '38px',
                                    boxShadow: '0 4px 12px rgba(30, 41, 59, 0.25)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: '#334155',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 6px 16px rgba(30, 41, 59, 0.3)',
                                    },
                                    '&:active': { transform: 'translateY(0px)' }
                                }}
                            >
                                <Plus size={20} strokeWidth={2.5} />
                            </IconButton>
                        </Tooltip>

                        {/* The Menu remains the same as before */}
                        <Menu
                            anchorEl={anchorSuppliesMenu}
                            open={Boolean(anchorSuppliesMenu)}
                            onClose={() => setAnchorSuppliesMenu(null)}
                            PaperProps={{
                                sx: {
                                    borderRadius: '12px',
                                    mt: 1,
                                    minWidth: 180,
                                    boxShadow: '0px 10px 25px rgba(0,0,0,0.2)',
                                }
                            }}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            <MenuItem
                                onClick={() => { setAnchorSuppliesMenu(null); setIsAddEssentialsOpen(true); }}
                                disabled={!authenticated}
                                sx={{ gap: 1.5, py: 1.2 }}
                            >
                                <i className="icon-magic-wand" style={{ color: '#9c27b0' }} />
                                <Typography variant="body2" fontWeight={500}>From My Essentials</Typography>
                            </MenuItem>

                            <MenuItem
                                onClick={() => { setAnchorSuppliesMenu(null); setIsAddSuggestionsOpen(true); }}
                                sx={{ gap: 1.5, py: 1.2 }}
                            >
                                <i className="icon-lamp-bright" style={{ color: '#ed6c02' }} />
                                <Typography variant="body2" fontWeight={500}>Smart Suggestions</Typography>
                            </MenuItem>

                            <Divider sx={{ my: 0.5, opacity: 0.6 }} />

                            <MenuItem
                                onClick={() => { setAnchorSuppliesMenu(null); setIsAddManualOpen(true); }}
                                sx={{ gap: 1.5, py: 1.2 }}
                            >
                                <i className="icon-pencil" style={{ color: '#2e7d32' }} />
                                <Typography variant="body2" fontWeight={500}>Manual Entry</Typography>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>
            </Paper>

            {/* --- UPDATED ATTENDEE BAR --- */}
            <div className="supplies-list-attendee-list-container" style={{
                display: 'flex',
                overflowX: 'auto',
                msOverflowStyle: 'none',
                alignItems: 'flex-start',
            }}>
                {sortedAttendees.map((attendee) => {
                    const attendeeAvatar = attendeesReferenceArray.find((_att) => _att.id === attendee.id);
                    const isSelected = selectedAttendee === attendee.id;
                    const fullName = attendee.nickname?.trim()
                        ? attendee.nickname
                        : `${attendee.fname} ${attendee.lname}`;

                    return (
                        <Box
                            key={attendee.id}
                            onClick={() => setSelectedAttendee(attendee.id)}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                cursor: 'pointer',
                                position: 'relative',
                                minWidth: '60px',
                                transition: 'transform 0.2s ease',
                                '&:hover': { transform: 'translateY(-2px)' }
                            }}
                        >
                            <Badge
                                badgeContent={attendee.supplyCount > 0 ? attendee.supplyCount : null}
                                sx={{
                                    '& .MuiBadge-badge': {
                                        backgroundColor: '#E0F2F1', // Soft Mint/Teal
                                        color: '#00695C',           // Dark Teal text
                                        fontSize: '10px',
                                        fontWeight: 900,
                                        border: '2px solid #fff',
                                        top: 5,
                                        right: 5
                                    },
                                }}
                            >
                                <Box sx={{
                                    p: '3px',
                                    borderRadius: '50%',
                                    border: isSelected ? '2px solid #151269' : '2px solid transparent',
                                    transition: 'all 0.3s ease'
                                }}>
                                    {!attendeeAvatar?.avatar || !attendeeAvatar?.avatar.preview ? (
                                        <Avatar
                                            {...stringAvatar(fullName)}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                border: `1px solid ${stringToColor(fullName)}`
                                            }}
                                        />
                                    ) : (
                                        <Avatar
                                            src={attendeeAvatar.avatar.preview}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                border: `1px solid ${stringToColor(fullName)}`
                                            }}
                                        />
                                    )}
                                </Box>
                            </Badge>

                            <Typography sx={{
                                fontSize: '10px',
                                fontWeight: isSelected ? 800 : 500,
                                mt: 0.5,
                                color: isSelected ? '#151269' : '#666',
                                textAlign: 'center',
                                maxWidth: '65px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>
                                {capitalizeFirstLetter(attendee.nickname || attendee.fname)}
                            </Typography>
                        </Box>
                    );
                })}

                {/* --- UNASSIGNED "BOUNTY" CIRCLE --- */}
                <Box
                    onClick={() => setSelectedAttendee("0")}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        minWidth: '60px',
                    }}
                >
                    <Badge
                        badgeContent={unassignedCount > 0 ? unassignedCount : null}
                        sx={{
                            '& .MuiBadge-badge': {
                                backgroundColor: '#FFEBEE',
                                color: '#C62828',
                                fontSize: '10px',
                                fontWeight: 900,
                                border: '2px solid #fff',
                                top: 5,
                                right: 5
                            },
                        }}
                    >
                        <Box sx={{
                            p: '3px',
                            borderRadius: '50%',
                            border: selectedAttendee === "0" ? '2px solid #C62828' : '2px solid transparent',
                        }}>
                            <Avatar sx={{
                                width: 32,
                                height: 32,
                                bgcolor: '#f5f5f5',
                                color: '#C62828',
                                border: '1px dashed #C62828'
                            }}>
                                <i className="icon-notification" style={{ fontSize: '16px' }} />
                            </Avatar>
                        </Box>
                    </Badge>
                    <Typography sx={{
                        fontSize: '10px',
                        fontWeight: selectedAttendee === "0" ? 800 : 500,
                        mt: 0.5,
                        color: selectedAttendee === "0" ? '#C62828' : '#666',
                    }}>
                        Bounty
                    </Typography>
                </Box>
            </div>


            {/* --- TABS & CONTENT --- */}
            <div className="supplies-list-items-container">
                <TabContext value={tabValue}>
                    <TabList
                        onChange={(e, v) => setTabValue(v)}
                        variant="fullWidth"
                        sx={{
                            px: 2,
                            pt: 1,
                            pb: 1,
                            minHeight: 48,
                            bgcolor: '#fff', // Or match your container background
                            '& .MuiTabs-indicator': {
                                display: 'none' // Removes the bottom line
                            }
                        }}
                    >
                        <Tab
                            label={`Supplies (${totalSupplies})`}
                            value="supplies"
                            sx={{
                                borderRadius: '12px',
                                fontWeight: 800,
                                fontSize: '13px',
                                textTransform: 'none',
                                color: '#95a5a6',
                                transition: 'all 0.2s',
                                '&.Mui-selected': {
                                    color: '#151269',
                                    bgcolor: '#f1f3f5' // Light grey pill background when active
                                },
                                '&:hover': {
                                    bgcolor: 'rgba(241, 243, 245, 0.5)'
                                }
                            }}
                        />
                        <Tab
                            label={`Food (${totalFood})`}
                            value="food"
                            sx={{
                                borderRadius: '12px',
                                fontWeight: 800,
                                fontSize: '13px',
                                textTransform: 'none',
                                color: '#95a5a6',
                                transition: 'all 0.2s',
                                '&.Mui-selected': {
                                    color: '#151269',
                                    bgcolor: '#f1f3f5'
                                },
                                '&:hover': {
                                    bgcolor: 'rgba(241, 243, 245, 0.5)'
                                }
                            }}
                        />
                    </TabList>

                    <TabPanel value="supplies" sx={{ p: 0 }}>
                        {Object.keys(groupedItems)
                            .filter(k => k.startsWith('supplies'))
                            .sort()
                            .map(key => (
                            <Box key={key}>
                                <Typography variant="overline" sx={{ px: 2, fontWeight: 900, color: '#95a5a6' }}>{key.split('_')[1].toUpperCase()}</Typography>
                                {groupedItems[key].map(renderSupplyItem)}
                            </Box>
                        ))}
                    </TabPanel>
                    <TabPanel value="food" sx={{ p: 0 }}>
                        {Object.keys(groupedItems)
                            .filter(k => k.startsWith('food'))
                            .sort()
                            .map(key => (
                            <Box key={key}>
                                <Typography variant="overline" sx={{ px: 2, fontWeight: 900, color: '#95a5a6' }}>{key.split('_')[1].toUpperCase()}</Typography>
                                {groupedItems[key].map(renderSupplyItem)}
                            </Box>
                        ))}
                    </TabPanel>
                </TabContext>
            </div>

            {/* --- DIALOGS --- */}
            <SuppliesAddNewDialog
                open={isAddManualOpen}
                onClose={() => setIsAddManualOpen(false)}
                selectedAttendeeId={selectedAttendee} />

            <SuppliesAddSuggestionsDialog
                open={isAddSuggestionsOpen}
                onClose={() => setIsAddSuggestionsOpen(false)}
                selectedAttendeeId={selectedAttendee} />

            <SuppliesAddEssentialsDialog
                open={isAddEssentialsOpen}
                onClose={() => setIsAddEssentialsOpen(false)}
                selectedAttendeeId={selectedAttendee} />

            <InfoSupplies
                open={isSuppliesInfoDialogOpen}
                setOpen={setSuppliesInfoDialogOpen} />

            <SuppliesEditItemDialog
                open={isEditDrawerOpen}
                onClose={() => setIsEditDrawerOpen(false)}
                item={editingItem}
                onSave={handleUpdateItem}
                onDelete={handleSupplyItemDelete}
            />

            {/* --- ITEM MENU --- */}
            <Menu anchorEl={anchorSupplyItemListMenu} open={Boolean(anchorSupplyItemListMenu)} onClose={() => setAnchorSupplyItemListMenu(null)}>
                <MenuItem onClick={() => handleSupplyItemDelete(selectedItemId)} sx={{ color: 'red' }}>
                    <ListItemIcon><i className="icon-bin" style={{ color: 'red' }} /></ListItemIcon>
                    <ListItemText primary="Delete Item" />
                </MenuItem>
            </Menu>
        </div>
    );
}

export default SuppliesList;